import { CalendarPlus } from "lucide-react";
import { useState } from "react";
import {
  ColoredOutlineButton,
  OUTLINE_ICON_BUTTON_ICON_CLASS,
} from "@/components/ui/colored-outline-button";
import { useLanguage } from "@/context/LanguageContext";
import { carDetailsLabels } from "@/i18n";
import { addAuctionToCalendar } from "@/utils/addToCalendar";
import type { AuctionCalendarEvent } from "@/utils/calendarUtils";
import { toast } from "sonner";

interface AddToCalendarButtonProps {
  event: AuctionCalendarEvent;
  className?: string;
}

export function AddToCalendarButton({
  event,
  className = "",
}: AddToCalendarButtonProps) {
  const { language } = useLanguage();
  const bi = carDetailsLabels(language).biddingInfo;
  const [isLoading, setIsLoading] = useState(false);
  const label = bi.addToCalendar ?? "Lägg till i kalender";

  const handleClick = async () => {
    setIsLoading(true);
    try {
      await addAuctionToCalendar(event);
      toast.success(bi.addToCalendarSuccess ?? "Kalenderhändelse skapad");
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        return;
      }
      console.error("Failed to add calendar event", error);
      toast.error(bi.addToCalendarError ?? "Kunde inte skapa kalenderhändelse");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ColoredOutlineButton
      type="button"
      size="icon"
      variant="calendar"
      onClick={handleClick}
      disabled={isLoading}
      isLoading={isLoading}
      aria-label={label}
      title={label}
      className={className}
    >
      <CalendarPlus
        className={OUTLINE_ICON_BUTTON_ICON_CLASS}
        strokeWidth={2.35}
      />
    </ColoredOutlineButton>
  );
}
